/**
 * メインレイアウトコンポーネント
 *
 * 認証済みユーザー向けの共通レイアウト（ヘッダー、サイドバー）
 */

import { Link, useLocation } from "react-router-dom";
import { useEffect } from "react";
import {
  AppShell,
  Title,
  Text,
  Stack,
  Group,
  Badge,
  NavLink,
  ActionIcon,
  useMantineColorScheme,
  Menu,
  Avatar,
  UnstyledButton,
  Tooltip,
  Transition,
} from "@mantine/core";
import { useMediaQuery, useLocalStorage } from "@mantine/hooks";
import { useAuth } from "../hooks/useAuth";
import { useAssistantContext } from "../contexts/AssistantContext";
import { AssistantPanel } from "./AssistantPanel";

import {
  Sun,
  Moon,
  LayoutDashboard,
  ShieldCheck,
  Users,
  FileText,
  Settings,
  LogOut,
  LayoutTemplate,
  ChevronLeft,
  ChevronRight,
  History,
  BarChart3,
  Sparkles,
  Book,
  Brain,
  Download,
} from "lucide-react";

interface NavigationProps {
  collapsed: boolean;
  onToggle: () => void;
}

/**
 * サイドバーナビゲーション
 */
function Navigation({ collapsed, onToggle }: NavigationProps) {
  const location = useLocation();
  const pathname = location.pathname;

  return (
    <Stack gap="xs" h="100%">
      <Stack gap="xs" style={{ flex: 1 }}>
        {/* 1. ダッシュボード */}
        <NavLink
          component={Link}
          to="/dashboard"
          label={collapsed ? null : "ダッシュボード"}
          leftSection={<LayoutDashboard size={20} />}
          active={pathname.startsWith("/dashboard")}
          variant="light"
          h={44}
          style={{ borderRadius: "8px" }}
        />

        {/* 2. 顧客管理 */}
        <NavLink
          component={Link}
          to="/customers"
          label={collapsed ? null : "顧客管理"}
          leftSection={<Users size={20} />}
          active={pathname.startsWith("/customers")}
          variant="light"
          h={44}
          style={{ borderRadius: "8px" }}
        />

        {/* 3. イベントログ */}
        <NavLink
          component={Link}
          to="/logs"
          label={collapsed ? null : "イベントログ"}
          leftSection={<FileText size={20} />}
          active={pathname.startsWith("/logs")}
          variant="light"
          h={44}
          style={{ borderRadius: "8px" }}
        />

        {/* 4. 操作ログ */}
        <NavLink
          component={Link}
          to="/operation-logs"
          label={collapsed ? null : "操作ログ"}
          leftSection={<History size={20} />}
          active={pathname.startsWith("/operation-logs")}
          variant="light"
          h={44}
          style={{ borderRadius: "8px" }}
        />

        {/* 5. 利用統計 */}
        <NavLink
          component={Link}
          to="/usage-stats"
          label={collapsed ? null : "利用統計"}
          leftSection={<BarChart3 size={20} />}
          active={pathname.startsWith("/usage-stats")}
          variant="light"
          h={44}
          style={{ borderRadius: "8px" }}
        />

        {/* 6. マニュアル */}
        <NavLink
          component={Link}
          to="/manual"
          label={collapsed ? null : "マニュアル"}
          leftSection={<Book size={20} />}
          active={pathname.startsWith("/manual")}
          variant="light"
          h={44}
          style={{ borderRadius: "8px" }}
        />

        {/* 管理者設定 */}
        <NavLink
          component={Link}
          to="/admins"
          label={collapsed ? null : "管理者設定"}
          leftSection={<ShieldCheck size={20} />}
          active={pathname.startsWith("/admins")}
          variant="light"
          h={44}
          style={{ borderRadius: "8px" }}
        />

        {/* クライアントインストーラー */}
        <NavLink
          component={Link}
          to="/installer"
          label={collapsed ? null : "インストーラー"}
          leftSection={<Download size={20} />}
          active={pathname.startsWith("/installer")}
          variant="light"
          h={44}
          style={{ borderRadius: "8px" }}
        />

        {/* 7. 設定（全管理者） */}
        {collapsed ? (
          // ミニサイドバー時: Menuでポップオーバー表示
          <Menu position="right-start" withArrow arrowPosition="center" offset={15}>
            <Menu.Target>
              <NavLink
                label={null}
                leftSection={<Settings size={20} />}
                active={pathname.startsWith("/templates") || pathname.startsWith("/ai-ocr-settings")}
                variant="light"
                h={44}
                style={{ borderRadius: "8px" }}
              />
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Label>設定</Menu.Label>
              <Menu.Item
                component={Link}
                to="/ai-ocr-settings"
                leftSection={<Brain size={16} />}
              >
                AI OCR設定
              </Menu.Item>
              <Menu.Item
                component={Link}
                to="/templates"
                leftSection={<LayoutTemplate size={16} />}
              >
                CSVテンプレート管理
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        ) : (
          // 通常時: アコーディオン表示
          <NavLink
            label="設定"
            leftSection={<Settings size={20} />}
            childrenOffset={12}
            h={44}
            style={{ borderRadius: "8px" }}
            defaultOpened={false}
          >
            <NavLink
              component={Link}
              to="/ai-ocr-settings"
              label="AI OCR設定"
              leftSection={<Brain size={18} />}
              active={pathname.startsWith("/ai-ocr-settings")}
              variant="light"
              h={44}
              style={{ borderRadius: "8px" }}
            />
            <NavLink
              component={Link}
              to="/templates"
              label="CSVテンプレート管理"
              leftSection={<LayoutTemplate size={18} />}
              active={pathname.startsWith("/templates")}
              variant="light"
              h={44}
              style={{ borderRadius: "8px" }}
            />
          </NavLink>
        )}
      </Stack>

      <ActionIcon 
        variant="subtle" 
        color="gray" 
        onClick={onToggle}
        size="lg"
        style={{ alignSelf: 'flex-end' }}
      >
        {collapsed ? <ChevronRight /> : <ChevronLeft />}
      </ActionIcon>
    </Stack>
  );
}

/**
 * テーマ切り替えボタン
 */
function ThemeToggle() {
  const { colorScheme, toggleColorScheme } = useMantineColorScheme();

  return (
    <ActionIcon
      variant="subtle"
      onClick={() => toggleColorScheme()}
      title={colorScheme === "dark" ? "ライトモードに切り替え" : "ダークモードに切り替え"}
    >
      {colorScheme === "dark" ? <Sun size={20} /> : <Moon size={20} />}
    </ActionIcon>
  );
}

interface MainLayoutProps {
  children: React.ReactNode;
}

/**
 * メインレイアウト
 */
export function MainLayout({ children }: MainLayoutProps) {
  const { user, role, isEmulator, signOut } = useAuth();
  const { panelOpened, togglePanel, closePanel } = useAssistantContext();
  const [collapsed, setCollapsed] = useLocalStorage({
    key: 'sidebar-collapsed',
    defaultValue: false,
  });

  // 画面幅が1024px未満の場合は自動的に折りたたむ
  const isNarrowScreen = useMediaQuery('(max-width: 1024px)');

  useEffect(() => {
    if (isNarrowScreen) {
      setCollapsed(true);
    }
  }, [isNarrowScreen, setCollapsed]);
  
  return (
    <AppShell
      header={{ height: 60 }}
      navbar={{ 
        width: collapsed ? 80 : 280,
        breakpoint: 0, // 自動で隠さない設定
      }}
      padding="md"
    >
      <AppShell.Header>
        <Group h="100%" px="md" justify="space-between">
          <Group>
            <img 
              src="/logo.svg" 
              alt="IBS OCR Logo" 
              style={{ height: 40 }} 
            />
            <Title order={3} visibleFrom="xs">IBS OCR</Title>
          </Group>
          <Group>
            {/* AIアシスタントボタン */}
            <Tooltip label="AIアシスタント">
              <ActionIcon
                variant={panelOpened ? "filled" : "subtle"}
                color="blue"
                size="lg"
                radius="xl"
                onClick={togglePanel}
              >
                <Sparkles size={20} strokeWidth={1.5} />
              </ActionIcon>
            </Tooltip>
            <ThemeToggle />
            {isEmulator && (
              <Badge color="yellow" variant="filled">
                Emulator
              </Badge>
            )}
            
            <Menu shadow="md" width={200} position="bottom-end">
              <Menu.Target>
                <UnstyledButton>
                  <Group gap="xs">
                    <Stack gap={0} align="flex-end" visibleFrom="xs">
                      <Text size="sm" fw={500}>
                        {user?.email}
                      </Text>
                      {role && (
                        <Text size="xs" c="dimmed">
                          管理者
                        </Text>
                      )}
                    </Stack>
                    <Avatar radius="xl" color="blue">
                      {(user?.email?.[0] || "U").toUpperCase()}
                    </Avatar>
                  </Group>
                </UnstyledButton>
              </Menu.Target>

              <Menu.Dropdown>
                <Menu.Label>アカウント設定</Menu.Label>
                <Menu.Item
                  color="red"
                  leftSection={<LogOut size={14} />}
                  onClick={signOut}
                >
                  ログアウト
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Group>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar p="md">
        <Navigation collapsed={collapsed} onToggle={() => setCollapsed(!collapsed)} />
      </AppShell.Navbar>

      <AppShell.Main 
        data-assistant-context
        style={{
          // アシスタントパネル表示時はメイン領域の右側にスペースを確保
          marginRight: panelOpened ? 512 : 0, // 480px + 32px余白
          transition: 'margin-right 300ms ease',
        }}
      >
        {children}
      </AppShell.Main>

      {/* AIアシスタントカラム（グレー背景） */}
      <Transition mounted={panelOpened} transition="slide-left" duration={300}>
        {(styles: React.CSSProperties) => (
          <div
            className="assistant-column"
            style={{
              ...styles,
              position: 'fixed',
              right: 0,
              top: 60, // ヘッダーの高さ
              bottom: 0,
              width: 512,
              zIndex: 150,
            }}
          />
        )}
      </Transition>

      {/* AIアシスタントパネル */}
      <AssistantPanel
        opened={panelOpened}
        onClose={closePanel}
      />
    </AppShell>
  );
}
